<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue';
import { RouterView, useRouter } from 'vue-router';

import { useErrorStore } from '@ohmybible/core';
import { UiToaster } from '@ohmybible/ui';

import AppFrame from '@/shared/components/AppFrame.vue';
import ErrorScreen from '@/shared/components/ErrorScreen.vue';

const router = useRouter();
const errorStore = useErrorStore();
const frame = useTemplateRef('frame');

/**
 * Page transitions follow the navigation stack (`meta.depth`): going deeper
 * slides in from the end, going back from the start, a sideways move fades.
 */
type Direction = 'forward' | 'back' | 'none';

const direction = ref<Direction>('none');

const removeAfterEach = router.afterEach((to, from) => {
  const delta = (to.meta.depth ?? 0) - (from.meta.depth ?? 0);

  direction.value = delta > 0 ? 'forward' : (delta < 0 ? 'back' : 'none');
});

onBeforeUnmount(removeAfterEach);

const transition = computed(() => ({
  enterActiveClass: 'transition duration-200 ease-standard',
  leaveActiveClass: 'transition duration-150 ease-in',
  enterFromClass: {
    forward: 'opacity-0 translate-x-6 rtl:-translate-x-6',
    back: 'opacity-0 -translate-x-6 rtl:translate-x-6',
    none: 'opacity-0',
  }[direction.value],
  leaveToClass: {
    forward: 'opacity-0 -translate-x-6 rtl:translate-x-6',
    back: 'opacity-0 translate-x-6 rtl:-translate-x-6',
    none: 'opacity-0',
  }[direction.value],
}));
</script>

<template>
  <AppFrame ref="frame">
    <ErrorScreen
      v-if="errorStore.error"
      :title="errorStore.error.title"
      :description="errorStore.error.description"
    />
    <RouterView
      v-else
      v-slot="{ Component, route }"
    >
      <Transition
        v-bind="transition"
        mode="out-in"
        @enter="frame?.restoreScroll()"
      >
        <component
          :is="Component"
          :key="route.path"
        />
      </Transition>
    </RouterView>

    <UiToaster />
  </AppFrame>
</template>
